import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput
} from '@mantine/core';
import { TbAlertCircle, TbFileDownload, TbSearch } from 'react-icons/tb';
import toast from 'react-hot-toast';
import authStore from '../../../../store/auth';
import { httpRequest } from '../../../../config/api';
import { useGetFundsByPeriodId } from '../../../../services/funds.service';

interface FormPFExportButtonProps {
  isDisabled?: boolean;
  isMinimalFiling?: boolean;
  isSignatureComplete?: boolean;
}

interface Fund {
  id: string;
  name: string;
}

function getFundLabel(fund: any): string {
  return fund?.fund_name || fund?.name || 'Unnamed fund';
}

export function FormPFExportButton({
  isDisabled = false,
  isMinimalFiling = false,
  isSignatureComplete = true
}: FormPFExportButtonProps) {
  const { periodId } = useParams() as { periodId?: string };
  const [isLoadingXml, setIsLoadingXml] = useState<boolean>(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const isLoading = isLoadingXml || isLoadingPdf;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);
  const token = authStore.token;

  const { data: fundsData = [], isLoading: isLoadingFunds } = useGetFundsByPeriodId({
    // @ts-ignore
    periodId,
    queryParams: {
      enabled: !!periodId && isOpen && !isMinimalFiling
    }
  });

  const funds = useMemo<Fund[]>(() => {
    // @ts-ignore
    return [...(fundsData || [])]
      .map(item => ({
        id: String(item.id),
        name: getFundLabel(item)
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [fundsData]);

  const filteredFunds = useMemo<Fund[]>(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      return funds;
    }

    return funds.filter(item => item.name.toLowerCase().includes(normalizedTerm));
  }, [funds, searchTerm]);

  const allFundIds = useMemo<string[]>(() => funds.map(item => item.id), [funds]);
  const allFundIdsKey = useMemo<string>(() => allFundIds.join('|'), [allFundIds]);

  const allSelected = funds.length > 0 && selectedFundIds.length === funds.length;
  const isIndeterminate = selectedFundIds.length > 0 && selectedFundIds.length < funds.length;

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFundIds(allFundIds);
    }
  }, [allFundIdsKey, isOpen]);

  const onClose = (): void => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const onToggleFund = (fundId: string): void => {
    setSelectedFundIds(prev =>
      prev.includes(fundId) ? prev.filter(item => item !== fundId) : [...prev, fundId]
    );
  };

  const onToggleAllFunds = (): void => {
    if (allSelected) {
      setSelectedFundIds([]);
    } else {
      setSelectedFundIds(allFundIds);
    }
  };

  const handleExportFormPF = async (): Promise<void> => {
    if (!isMinimalFiling && selectedFundIds.length === 0) {
      toast.error('Select at least one fund to export');
      return;
    }

    try {
      setIsLoadingXml(true);

      const queryString = new URLSearchParams();
      if (!isMinimalFiling) {
        selectedFundIds.forEach(fundId => {
          queryString.append('fund_ids', fundId);
        });
      }

      const url = `${import.meta.env.VITE_BASE_URL}api/report/export-form-pf/${periodId}${queryString.toString() ? `?${queryString.toString()}` : ''}`;
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await httpRequest.get(url, {
        headers,
        responseType: 'blob'
      });

      // Create blob and download
      // @ts-ignore
      const blob = new Blob([response], { type: 'application/xml' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Extract filename from response headers if available, otherwise use default
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `FormPF_Export_${timestamp}.xml`;

      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Form PF XML exported successfully');
      onClose();
    } catch (err: unknown) {
      console.error('Error exporting Form PF XML:', err);
      if ((err as any)?.status === 404) {
        toast.error('No Form PF data found for the selected funds');
      } else if ((err as any)?.status === 403) {
        toast.error('Access denied');
      } else {
        toast.error('Error exporting Form PF XML');
      }
    } finally {
      setIsLoadingXml(false);
    }
  };

  const handleExportFormPFPdf = async (): Promise<void> => {
    if (!isMinimalFiling && selectedFundIds.length === 0) {
      toast.error('Select at least one fund to export');
      return;
    }

    try {
      setIsLoadingPdf(true);

      const queryString = new URLSearchParams();
      if (!isMinimalFiling) {
        selectedFundIds.forEach(fundId => {
          queryString.append('fund_ids', fundId);
        });
      }

      const url = `${import.meta.env.VITE_BASE_URL}api/report/export-form-pf-pdf/${periodId}${queryString.toString() ? `?${queryString.toString()}` : ''}`;
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await httpRequest.get(url, {
        headers,
        responseType: 'blob'
      });
      //@ts-ignore
      const blob = new Blob([response], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `FormPF_Export_${timestamp}.pdf`;

      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Form PF PDF exported successfully');
      onClose();
    } catch (err: unknown) {
      console.error('Error exporting Form PF PDF:', err);
      if ((err as any)?.status === 404) {
        toast.error('No Form PF data found for the selected funds');
      } else if ((err as any)?.status === 403) {
        toast.error('Access denied');
      } else {
        toast.error('Error exporting Form PF PDF');
      }
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return (
    <>
      <Button
        id="form-pf-export-button-trigger"
        size="sm"
        variant="subtle"
        leftSection={<TbFileDownload />}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      >
        Export
      </Button>

      <Drawer
        id="form-pf-export-drawer"
        opened={isOpen}
        onClose={onClose}
        title="Export Form PF"
        size="lg"
        position="right"
        closeOnClickOutside
        overlayProps={{
          backgroundOpacity: 0.35,
          blur: 3
        }}
      >
        <Stack id="form-pf-export-drawer-stack" gap="sm">
          {isMinimalFiling ? (
            <>
              <Alert
                id="form-pf-export-minimal-filing-alert"
                icon={<TbAlertCircle />}
                title="Minimal Filing Export"
                color="blue"
                variant="light"
              >
                This is a minimal filing (PF-TRANS or PF-FINAL). The export will include only the
                filing header and Section 1a signature. Make sure the signature fields are saved in
                the report before exporting.
              </Alert>
              {!isSignatureComplete && (
                <Alert
                  id="form-pf-export-signature-alert"
                  icon={<TbAlertCircle />}
                  color="red"
                  variant="light"
                >
                  All signature fields (Name, Signature, Title, Email, Phone, Date) must be filled
                  before exporting.
                </Alert>
              )}
            </>
          ) : (
            <Alert
              id="form-pf-export-info-alert"
              icon={<TbAlertCircle />}
              title="Form PF Export"
              color="blue"
              variant="light"
            >
              Choose the funds to include in this Form PF export.
            </Alert>
          )}

          {!isMinimalFiling && (
            <Stack id="form-pf-export-funds-stack" gap="sm">
              <Flex
                id="form-pf-export-funds-header"
                justify="space-between"
                align="center"
                wrap="wrap"
                gap="sm"
              >
                <Box id="form-pf-export-funds-header-copy">
                  <Text id="form-pf-export-funds-title" fw={600}>
                    Funds in this reporting period
                  </Text>
                  {funds.length > 0 && selectedFundIds.length === 0 ? (
                    <Text id="form-pf-export-funds-warning" size="sm" c="red.6">
                      Select at least one fund to enable export.
                    </Text>
                  ) : (
                    <Text id="form-pf-export-funds-helper" size="sm" c="dimmed">
                      Select one or more funds to include in the export.
                    </Text>
                  )}
                </Box>

                <Badge
                  id="form-pf-export-selected-badge"
                  color={selectedFundIds.length > 0 ? 'blue' : 'gray'}
                  variant="light"
                  size="lg"
                >
                  {selectedFundIds.length} of {funds.length} selected
                </Badge>
              </Flex>

              <TextInput
                id="form-pf-export-search-input"
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.currentTarget.value)
                }
                placeholder="Search funds"
                leftSection={<TbSearch size={16} />}
                disabled={isLoadingFunds || funds.length === 0}
              />

              <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
                <Checkbox
                  id="form-pf-export-select-all-checkbox"
                  label="Select all funds"
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={onToggleAllFunds}
                  disabled={isLoadingFunds || funds.length === 0}
                />

                <Text id="form-pf-export-filtered-count" size="sm" c="dimmed">
                  {filteredFunds.length} fund(s) shown
                </Text>
              </Flex>

              <Paper
                id="form-pf-export-funds-paper"
                withBorder
                radius="md"
                p="0"
                style={{ overflow: 'hidden' }}
              >
                {isLoadingFunds ? (
                  <Flex
                    id="form-pf-export-loading-state"
                    direction="column"
                    align="center"
                    justify="center"
                    gap="sm"
                    p="xl"
                  >
                    <Loader size="sm" />
                    <Text id="form-pf-export-loading-text" size="sm" c="dimmed">
                      Loading funds...
                    </Text>
                  </Flex>
                ) : funds.length === 0 ? (
                  <Box id="form-pf-export-empty-state" p="lg">
                    <Text id="form-pf-export-empty-state-text" size="sm" c="dimmed">
                      No funds are available for this reporting period.
                    </Text>
                  </Box>
                ) : filteredFunds.length === 0 ? (
                  <Box id="form-pf-export-no-results-state" p="lg">
                    <Text id="form-pf-export-no-results-text" size="sm" c="dimmed">
                      No funds match your search.
                    </Text>
                  </Box>
                ) : (
                  <ScrollArea id="form-pf-export-scroll-area" h={220}>
                    <Stack id="form-pf-export-fund-list" gap="0">
                      {filteredFunds.map((fund: Fund, index: number) => (
                        <Box
                          id={`form-pf-export-fund-item-${fund.id}`}
                          key={fund.id}
                          p="sm"
                          style={{
                            borderTop: index === 0 ? 'none' : '1px solid #E9ECEF',
                            backgroundColor: selectedFundIds.includes(fund.id)
                              ? '#F8FBFF'
                              : '#FFFFFF'
                          }}
                        >
                          <Checkbox
                            id={`form-pf-export-fund-checkbox-${fund.id}`}
                            label={fund.name}
                            checked={selectedFundIds.includes(fund.id)}
                            onChange={() => onToggleFund(fund.id)}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </ScrollArea>
                )}
              </Paper>
            </Stack>
          )}

          <Flex id="form-pf-export-actions" gap="sm" wrap="wrap">
            <Button
              id="form-pf-export-xml-button"
              style={{ flex: 1, minWidth: 160 }}
              onClick={handleExportFormPF}
              loading={isLoadingXml}
              color="blue"
              disabled={
                (isMinimalFiling && !isSignatureComplete) ||
                (!isMinimalFiling &&
                  (isLoadingFunds ||
                    funds.length === 0 ||
                    selectedFundIds.length === 0 ||
                    isLoadingPdf))
              }
            >
              Download XML
            </Button>
            <Button
              id="form-pf-export-pdf-button"
              style={{ flex: 1, minWidth: 160 }}
              onClick={handleExportFormPFPdf}
              loading={isLoadingPdf}
              color="blue"
              variant="outline"
              disabled={
                (isMinimalFiling && !isSignatureComplete) ||
                (!isMinimalFiling &&
                  (isLoadingFunds ||
                    funds.length === 0 ||
                    selectedFundIds.length === 0 ||
                    isLoadingXml))
              }
            >
              Download PDF
            </Button>
            <Button
              id="form-pf-export-cancel-button"
              style={{ flex: 1, minWidth: 160 }}
              variant="light"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Flex>
        </Stack>
      </Drawer>
    </>
  );
}
